import { useState, useEffect } from "react";
import {
  sendCompanySearch,
  sendCompanySearchBackUp,
  validateURL,
  searchCompanyForCorrectUrl,
} from "../../api/cases";
import { useCompanyContext } from "../../context/CompanyContext"; // adjust path if needed
import LoadingSpinner from "../Universal/LoadingSpinner";
import {
  Search,
  X,
  Globe,
  Building2,
  ExternalLink,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { Input } from "../Universal/Input";
import { Button } from "../Universal/Button";

const PopUpSearch = (props) => {
  const { addCompany, selectCompany, refreshCompanies, setShowAddCompany } =
    useCompanyContext();

  // Burst Mode is intentionally dormant until the feature is ready.
  // const isDD = process.env.REACT_APP_IS_DD === "true";

  const [searchInput, setSearchInput] = useState("");
  const [searchResponse, setSearchResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [detectedMode, setDetectedMode] = useState(null); // 'url' | 'company' | null

  const [urlSearchResults, setUrlSearchResults] = useState([]);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [urlPreviewBase, setUrlPreviewBase] = useState(null);
  const [urlPreviewBrowser, setUrlPreviewBrowser] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(false);

  const [pendingCompany, setPendingCompany] = useState(null);
  // const [burst, setBurst] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key !== "Escape") return;

      if (pendingCompany) {
        closeConfirmAdd();
        return;
      }

      setShowAddCompany(false);
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [pendingCompany]);

  useEffect(() => {
    setLoadingPreview(false);
    setUrlPreviewBase(null);
    setUrlPreviewBrowser(null);
    setIsValidUrl(false);
    setPreviewError(false);
  }, [searchInput]);

  function isLikelyUrl(input) {
    return /^(https?:\/\/|www\.|[a-zA-Z0-9-]+\.[a-zA-Z]{2,})/.test(
      input.trim(),
    );
  }

  function removeSeparator(str) {
    return str.replace(/\s*[-|]\s*LinkedIn$/i, "").trim();
  }

  function normalizeDomainForDisplay(value) {
    if (!value) return "";

    try {
      const url = value.startsWith("http") ? value : `https://${value}`;
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return value.replace(/^https?:\/\//, "").replace(/^www\./, "");
    }
  }

  function handleSearch() {
    const input = searchInput.trim();
    if (!input) return;

    if (isLikelyUrl(input)) {
      setDetectedMode("url");
      setSearchResponse(null);
      searchByUrl(input);
    } else {
      setDetectedMode("company");
      setUrlSearchResults([]);
      setSelectedUrl(null);
      setUrlPreviewBase(null);
      setUrlPreviewBrowser(null);
      searchForCompany(input);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") handleSearch();
  }

  function searchForCompany(company) {
    setIsLoading(true);
    sendCompanySearch(company)
      .then((response) => {
        setSearchResponse(response.data);
        setIsLoading(false);
      })
      .catch(() => {
        setSearchResponse([]);
        setIsLoading(false);
      });
  }

  function backup_search(company) {
    setIsLoading(true);
    sendCompanySearchBackUp(company).then((response) => {
      setSearchResponse(response.data);
      setIsLoading(false);
    });
  }

  function openConfirmAdd(company) {
    setPendingCompany(company);
    // setBurst(false);
    setIsAdding(false);
    setAddSuccess(false);
  }

  function closeConfirmAdd() {
    if (isAdding) return;

    setPendingCompany(null);
    // setBurst(false);
    setIsAdding(false);
    setAddSuccess(false);
  }

  async function confirmAddCompany() {
    if (!pendingCompany) return;

    setIsAdding(true);
    setAddSuccess(false);

    try {
      const payload = {
        name: pendingCompany.name,
        domain: pendingCompany.domain,
        // Burst Mode intentionally omitted until the feature is ready.
        // ...(isDD ? { burst } : {}),
      };

      await addCompany(payload);

      const updated = await refreshCompanies();
      const added = updated.find((c) => c.case === pendingCompany.name);

      if (added) selectCompany(added);

      setAddSuccess(true);

      window.setTimeout(() => {
        setShowAddCompany(false);
        setPendingCompany(null);
        // setBurst(false);
        setIsAdding(false);
        setAddSuccess(false);
      }, 650);
    } catch (err) {
      console.error("Failed to add company:", err);
      setIsAdding(false);
      setAddSuccess(false);
    }
  }

  function searchByUrl(input) {
    if (isLikelyUrl(input)) {
      validateSpecificUrl(input);
    } else {
      searchForRelativeUrls(input);
    }
  }

  function validateSpecificUrl(url) {
    setLoadingPreview(true);
    setUrlPreviewBase(null);
    setUrlPreviewBrowser(null);
    setPreviewError(false);
    setUrlSearchResults([]);
    setSelectedUrl(url);

    const searchQuery = url.startsWith("http") ? url : "https://www." + url;

    validateURL(searchQuery).then((response) => {
      if (response.data.screenshot) {
        setUrlPreviewBase(response.data.screenshot);
        setUrlPreviewBrowser(response.data.browserHtml);
        setIsValidUrl(true);
        setLoadingPreview(false);
      } else if (response.data.message === "fail") {
        setPreviewError(true);
        setLoadingPreview(false);
      }
    });
  }

  function searchForRelativeUrls(query) {
    setLoadingPreview(true);
    setUrlSearchResults([]);
    setSelectedUrl(null);

    searchCompanyForCorrectUrl(query).then((response) => {
      setUrlSearchResults(response.data);
      if (response.data.length > 0) loadUrlPreview(response.data[0].url);
      setLoadingPreview(false);
    });
  }

  function loadUrlPreview(url) {
    setSelectedUrl(url);
    setLoadingPreview(true);
    setUrlPreviewBase(null);
    setUrlPreviewBrowser(null);
    setPreviewError(false);

    const searchQuery = "https://www." + url;

    validateURL(searchQuery).then((response) => {
      if (response.data.screenshot) {
        setUrlPreviewBase(response.data.screenshot);
        setUrlPreviewBrowser(response.data.browserHtml);
        setLoadingPreview(false);
      } else if (response.data.message === "fail") {
        setPreviewError(true);
        setLoadingPreview(false);
      }
    });
  }

  function clickScreenshot(source) {
    props.setIsScreenshotPopUpOpen(true);
    props.setCurrentScreenshotForPopUp(source);
  }

  function handleCompanyResultAdd(item) {
    const name = removeSeparator(item.linkedin_name);

    openConfirmAdd({
      name,
      domain: item.linkedin_url,
      displayName: name,
      displayDomain: item.linkedin_url,
      description: item.snippet,
      sourceLabel: "Company search result",
    });
  }

  function handleUrlSearchAdd() {
    openConfirmAdd({
      name: selectedUrl,
      domain: searchInput,
      displayName: normalizeDomainForDisplay(selectedUrl),
      displayDomain: selectedUrl,
      description: urlPreviewBrowser,
      sourceLabel: "URL result",
    });
  }

  const hasPreview = urlPreviewBase || loadingPreview || previewError;

  return (
    /* ── Backdrop ── */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-mainBg/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      {/* ── Modal panel ── */}
      <div
        className="relative w-full max-w-2xl z-[9999999999] max-h-[85vh] flex flex-col rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-mainDivider">
          <Building2 className="h-5 w-5 text-velBlue" aria-hidden="true" />
          <h2 className="text-lg font-medium text-mainText">Add Company</h2>
          <button
            onClick={() => setShowAddCompany(false)}
            className="ml-auto p-1.5 rounded-lg text-mutedText transition hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Search bar ── */}
        <div className="px-6 py-4 border-b border-mainDivider">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mutedText">
                <Search className="h-4 w-4" aria-hidden="true" />
              </div>
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Company name or URL…"
                className="pl-8 w-full"
                autoFocus
              />
            </div>
            <Button onClick={handleSearch} disabled={!searchInput.trim()}>
              Search
            </Button>
          </div>
          {detectedMode && (
            <p className="mt-2 text-xs text-mutedText">
              {detectedMode === "url"
                ? "URL detected — validating site"
                : "Searching company databases"}
            </p>
          )}
        </div>

        {/* ── Results area ── */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {/* ── Company results ── */}
          {detectedMode === "company" && (
            <div>
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <LoadingSpinner />
                </div>
              ) : searchResponse && searchResponse.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {searchResponse.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start justify-between gap-4 rounded-lg border border-inputBorder bg-innerBg px-5 py-4"
                    >
                      <div className="flex flex-col gap-1.5 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-mainText truncate">
                            {removeSeparator(item.linkedin_name)}
                          </p>
                          {item.followers && (
                            <span className="text-xs text-mutedText shrink-0">
                              {item.followers}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-velBlue truncate">
                          {item.linkedin_url}
                        </p>
                        {item.snippet && (
                          <p className="text-sm text-mutedText line-clamp-2">
                            {item.snippet}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleCompanyResultAdd(item)}
                        className="px-3.5 py-1.5 rounded-lg border text-sm shrink-0 transition border-velBlue/30 text-velBlue hover:bg-velBlue/10 outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              ) : searchResponse && searchResponse.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Search
                    className="h-8 w-8 text-mutedText/40"
                    aria-hidden="true"
                  />
                  <p className="text-sm text-mutedText">No results found</p>
                  <button
                    onClick={() => backup_search(searchInput)}
                    className="text-sm text-velBlue hover:underline cursor-pointer"
                  >
                    Try extended search
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center py-16 text-mutedText text-sm">
                  Search for a company
                </div>
              )}
            </div>
          )}

          {/* ── URL results ── */}
          {detectedMode === "url" && (
            <div className="flex flex-col gap-4">
              {/* URL result list */}
              {urlSearchResults.length > 0 && (
                <div className="rounded-lg border border-surfaceBorder bg-surface/80 backdrop-blur-md overflow-hidden">
                  <div className="px-5 py-3 text-sm text-mutedText border-b border-mainDivider">
                    {urlSearchResults.length} matching site
                    {urlSearchResults.length !== 1 ? "s" : ""}
                  </div>
                  <div className="divide-y divide-mainDivider">
                    {urlSearchResults.map((item, index) => {
                      const active = selectedUrl === item.url;
                      return (
                        <button
                          key={index}
                          onClick={() => loadUrlPreview(item.url)}
                          className={[
                            "w-full text-left px-5 py-4 transition flex items-start justify-between gap-4",
                            active
                              ? "bg-selectedSection"
                              : "hover:bg-tableRowHover",
                          ].join(" ")}
                        >
                          <div className="flex flex-col gap-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Globe
                                className={`h-3.5 w-3.5 shrink-0 ${
                                  active ? "text-velBlue" : "text-mutedText"
                                }`}
                                aria-hidden="true"
                              />
                              <p
                                className={`text-sm font-medium truncate ${
                                  active ? "text-velBlue" : "text-mainText"
                                }`}
                              >
                                {item.url}
                              </p>
                            </div>
                            {item.title && (
                              <p className="text-xs text-mutedText pl-[22px] truncate">
                                {item.title}
                              </p>
                            )}
                            {item.snippet && (
                              <p className="text-xs text-mutedText/60 pl-[22px] line-clamp-1">
                                {item.snippet}
                              </p>
                            )}
                          </div>
                          {active && (
                            <span className="text-[11px] text-velBlue shrink-0 mt-0.5">
                              Selected
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Preview section */}
              {hasPreview && (
                <div className="rounded-lg border border-surfaceBorder bg-surface/80 backdrop-blur-md p-5">
                  {loadingPreview ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : previewError ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-2">
                      <X
                        className="h-8 w-8 text-errorText"
                        aria-hidden="true"
                      />
                      <p className="text-sm text-errorText">
                        Could not load preview for this URL
                      </p>
                    </div>
                  ) : urlPreviewBase ? (
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Globe
                            className="h-4 w-4 text-velBlue"
                            aria-hidden="true"
                          />
                          <p className="text-sm font-medium text-mainText">
                            {selectedUrl}
                          </p>
                        </div>
                        <a
                          href={
                            selectedUrl?.startsWith("http")
                              ? selectedUrl
                              : `https://www.${selectedUrl}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-mutedText hover:text-velBlue transition cursor-pointer"
                        >
                          Open <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                      <img
                        src={`data:image/png;base64,${urlPreviewBase}`}
                        onClick={() =>
                          clickScreenshot(
                            `data:image/png;base64,${urlPreviewBase}`,
                          )
                        }
                        alt="Site preview"
                        className="w-full max-h-[40vh] object-contain object-top cursor-pointer rounded-lg border border-surfaceBorder bg-innerBg"
                      />
                      {urlPreviewBrowser && (
                        <div className="rounded-lg border border-inputBorder bg-innerBg p-4 max-h-[20vh] overflow-auto">
                          <p className="text-xs text-mutedText whitespace-pre-line leading-relaxed">
                            {urlPreviewBrowser}
                          </p>
                        </div>
                      )}
                      <div className="pt-2">
                        <Button onClick={handleUrlSearchAdd} fullWidth>
                          Add this company
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Empty state for direct URL not yet previewed */}
              {!hasPreview &&
                urlSearchResults.length === 0 &&
                !loadingPreview && (
                  <div className="rounded-lg border border-surfaceBorder bg-surface/80 backdrop-blur-md flex flex-col items-center justify-center py-16 gap-3">
                    <Globe
                      className="h-8 w-8 text-mutedText/40"
                      aria-hidden="true"
                    />
                    <p className="text-sm text-mutedText">Validating URL…</p>
                  </div>
                )}
            </div>
          )}

          {/* ── Initial empty state ── */}
          {!detectedMode && (
            <div className="rounded-lg border border-surfaceBorder bg-surface/80 backdrop-blur-md flex flex-col items-center justify-center py-20 gap-3">
              <Search
                className="h-10 w-10 text-mutedText/40"
                aria-hidden="true"
              />
              <p className="text-sm text-mutedText">
                Search by company name or paste a URL to get started
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Confirm Add modal ── */}
      {pendingCompany && (
        <div
          className="fixed inset-0 z-[10000000000] flex items-center justify-center p-6 bg-mainBg/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={closeConfirmAdd}
        >
          <div
            className="relative w-full max-w-md rounded-lg border border-surfaceBorder bg-surface/95 shadow-login-panel backdrop-blur-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-velBlue/70 to-transparent" />

            <div className="px-6 py-5 border-b border-mainDivider">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-velBlue/30 bg-velBlue/10 text-velBlue">
                  {addSuccess ? (
                    <CheckCircle2 className="h-5 w-5" strokeWidth={2} />
                  ) : (
                    <Building2 className="h-5 w-5" strokeWidth={1.8} />
                  )}
                </div>

                <div className="min-w-0">
                  <h3 className="text-base font-medium text-mainText">
                    {addSuccess ? "Company added" : "Add this company?"}
                  </h3>
                  <p className="mt-1 text-sm text-mutedText leading-relaxed">
                    {addSuccess
                      ? `${pendingCompany.displayName || pendingCompany.name} has been added to your active companies.`
                      : "Confirm that you want to add this company."}
                  </p>
                </div>

                <button
                  onClick={closeConfirmAdd}
                  disabled={isAdding}
                  className="ml-auto p-1.5 rounded-lg text-mutedText transition hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Close confirmation"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="px-6 py-5 flex flex-col gap-4">
              <div className="rounded-lg border border-inputBorder bg-innerBg px-4 py-3">
                <div className="flex items-start gap-3">
                  <Globe
                    className="mt-0.5 h-4 w-4 shrink-0 text-velBlue"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-mainText truncate">
                      {pendingCompany.displayName || pendingCompany.name}
                    </p>
                    {pendingCompany.displayDomain && (
                      <p className="mt-0.5 text-xs text-velBlue truncate">
                        {pendingCompany.displayDomain}
                      </p>
                    )}
                    {pendingCompany.description && (
                      <p className="mt-2 text-xs text-mutedText line-clamp-2">
                        {pendingCompany.description}
                      </p>
                    )}
                    {pendingCompany.sourceLabel && (
                      <p className="mt-2 text-[11px] uppercase tracking-widest text-mutedText/50">
                        {pendingCompany.sourceLabel}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Burst Mode UI intentionally hidden until the feature is ready.
              {isDD && !addSuccess && (
                <div className="rounded-lg border border-inputBorder bg-innerBg px-4 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-mainText">
                          Burst Mode
                        </p>
                        <span
                          title="PLACEHOLDER: Product should explain what Burst Mode does and when users should enable it."
                          className="inline-flex cursor-help text-mutedText hover:text-mainText transition"
                        >
                          <Info className="h-3.5 w-3.5" strokeWidth={2} />
                        </span>
                      </div>

                      <p className="mt-1 text-xs text-mutedText leading-relaxed">
                        PLACEHOLDER: Enable Burst Mode for a more intensive
                        initial company setup.
                      </p>
                    </div>

                    <button
                      type="button"
                      role="switch"
                      aria-checked={burst}
                      onClick={() => setBurst((v) => !v)}
                      className="relative mt-0.5 inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 outline-none ring-inputFocus/60 focus-visible:ring-2"
                      style={{
                        background: burst ? "rgb(14,165,233)" : undefined,
                        boxShadow: burst
                          ? "0 0 8px rgba(14,165,233,0.28)"
                          : undefined,
                      }}
                    >
                      <span
                        className={[
                          "absolute inset-0 rounded-full transition-colors duration-200",
                          burst ? "" : "bg-mainButtonBg",
                        ].join(" ")}
                      />
                      <span
                        className={[
                          "absolute top-[3px] h-[14px] w-[14px] rounded-full bg-white shadow-sm z-10 transition-all duration-200",
                          burst ? "left-[19px]" : "left-[3px]",
                        ].join(" ")}
                      />
                    </button>
                  </div>
                </div>
              )}
              */}
            </div>

            {!addSuccess && (
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-mainDivider bg-innerBg/40">
                <button
                  type="button"
                  onClick={closeConfirmAdd}
                  disabled={isAdding}
                  className="px-3.5 py-1.5 rounded-lg border border-inputBorder text-sm text-mutedText hover:bg-btnHover hover:text-mainText transition outline-none ring-inputFocus/60 focus-visible:ring-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmAddCompany}
                  disabled={isAdding}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg border border-velBlue/30 bg-velBlue/10 text-sm text-velBlue hover:bg-velBlue/15 transition outline-none ring-inputFocus/60 focus-visible:ring-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isAdding && (
                    <Loader2
                      className="h-3.5 w-3.5 animate-spin"
                      strokeWidth={2}
                    />
                  )}
                  {isAdding ? "Adding…" : "Confirm"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PopUpSearch;
